import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { eur } from "@/lib/apiClient";
import { catalogFor } from "@/lib/productCatalog";
import { useTrattativa } from "@/context/TrattativaContext";

const KPI = [
  { k: "pratiche", t: "Pratiche aperte", to: "/preventivi?vista=aperte", color: "#1B2380", v: (s) => s.pratiche_aperte },
  { k: "nota", t: "Note in attesa", to: "/proposte", color: "#2A62A0", v: (s) => s.stages?.nota?.count, sub: (s) => eur(s.stages?.nota?.premio || 0) },
  { k: "attesa_bonifico", t: "Attesa bonifico", to: "/proposte", color: "#F39200", v: (s) => s.stages?.attesa_bonifico?.count, sub: (s) => eur(s.stages?.attesa_bonifico?.premio || 0) },
  { k: "bonificata", t: "Bonificate", to: "/proposte", color: "#5E9732", v: (s) => s.stages?.bonificata?.count, sub: (s) => eur(s.stages?.bonificata?.premio || 0) },
  { k: "saldo", t: "Saldo provvigioni", to: "/provvigioni", color: "#0E8C84", v: (s) => eur(s.saldo_provvigioni || 0), sub: () => "maturate, non ancora pagate" },
];

export function KpiTiles({ summary }) {
  const s = summary || {};
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
      {KPI.map((x, i) => (
        <Link key={x.k} to={x.to} data-testid={`home-kpi-${x.k}`} style={{ borderTopColor: x.color, animationDelay: `${i * 60}ms` }}
          className="rounded-2xl border-t-4 bg-white p-4 animate-in fade-in slide-in-from-bottom-2 transition-shadow hover:shadow-lg">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{x.t}</div>
          <div data-testid={`home-kpi-${x.k}-value`} className="mt-2 font-heading text-2xl font-semibold text-forest">{summary ? (x.v(s) ?? 0) : "–"}</div>
          {x.sub && summary && <div className="text-xs text-muted-foreground">{x.sub(s)}</div>}
        </Link>
      ))}
    </div>
  );
}

function ProductCard({ p, color, line }) {
  const t = useTrattativa();
  const navigate = useNavigate();
  const calc = () => (p.external ? window.open(p.external, "_blank", "noopener") : navigate(t.current && p.to.startsWith("/quota") ? `${p.to}?anagrafica=${t.current.anagrafica_id}` : p.to));
  return (
    <div data-testid={`catalog-card-${p.code}`} className={`flex flex-col rounded-2xl border bg-white p-5 transition-shadow ${p.soon ? "opacity-60" : "hover:shadow-xl"}`}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color }}>{line}</div>
        {p.soon && <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">In arrivo</span>}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl" style={{ background: `${color}1A` }}><p.icon className="h-6 w-6" style={{ color }} /></div>
        <div><div className="font-heading text-lg font-semibold leading-tight text-forest">{p.label}</div>{p.sub && <div className="text-xs text-muted-foreground">{p.sub}</div>}</div>
      </div>
      <div className="mt-3 text-sm text-muted-foreground">Per: {p.who}</div>
      <div className="mt-3 flex flex-wrap gap-1.5">{p.g.map((g) => <span key={g} className="rounded-md border px-2 py-0.5 text-[11px] text-muted-foreground">{g}</span>)}</div>
      {!p.soon && (
        <div className="mt-auto flex gap-2 pt-5">
          <button onClick={calc} data-testid={`catalog-calc-${p.code}`} className="flex-1 rounded-lg border border-forest px-3 py-2 text-xs font-semibold text-forest transition-colors hover:bg-sage">Calcola premio</button>
          <button onClick={() => t.add(p.code)} data-testid={`catalog-add-${p.code}`} className="flex-1 rounded-lg bg-terracotta px-3 py-2 text-[11px] font-bold uppercase text-white transition-colors hover:bg-terracotta-hover">Aggiungi alla trattativa</button>
        </div>
      )}
    </div>
  );
}

export function Catalog({ user }) {
  const [line, setLine] = useState("all");
  const lines = catalogFor(user);
  const list = lines.filter((l) => line === "all" || l.key === line).flatMap((l) => l.items.map((p) => ({ p, l })));
  return (
    <div className="space-y-6" data-testid="home-catalog">
      <div className="grid gap-5 md:grid-cols-2">
        {lines.map((l) => (
          <button key={l.key} onClick={() => setLine(l.key)} data-testid={`home-sector-${l.key}`} className="group relative overflow-hidden rounded-2xl bg-forest p-7 text-left text-white">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full transition-transform duration-300 group-hover:scale-110" style={{ background: l.color }} />
            <div className="relative text-[11px] font-bold uppercase tracking-wider text-white/60">{l.group}</div>
            <div className="relative mt-2 max-w-[75%] font-heading text-2xl font-semibold leading-tight">{l.title}</div>
            <div className="relative mt-4 flex items-center gap-1 text-sm text-white/80">{l.items.filter((i) => !i.soon).length} prodotti attivi per te <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></div>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {[["all", "Tutti i prodotti"], ...lines.map((l) => [l.key, l.short])].map(([k, t]) => (
          <button key={k} onClick={() => setLine(k)} data-testid={`home-chip-${k}`}
            className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-colors ${line === k ? "border-forest bg-forest text-white" : "border-forest/30 text-forest hover:bg-sage"}`}>{t}</button>
        ))}
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {list.map(({ p, l }) => <ProductCard key={p.code} p={p} color={l.color} line={l.short} />)}
      </div>
    </div>
  );
}
